/**
 * StepThreadChoice.jsx (Replace path)
 *
 * Tool Finder REPLACE path multi-thread picker. Fires after
 * EQUIPMENT_SELECT when the picked rig's `threadSystem.requiresUserChoice`
 * is true and it has multiple thread alternates.
 *
 * Distinct from `StepEquipmentThreadChoice.jsx` — that is the EXPLORE-path
 * sub-step driven by EQUIPMENT_CLASSES thread/shank choices. This one
 * reads the rig directly via `selections.rigModel.threadSystem`.
 *
 * PORTED from TH ToolFinder v4:
 *   `tophammer-toolfinder-v4/src/components/wizard/StepThreadChoice.jsx`
 *   (332 lines). The 16×16 thread badge with sandvik-gold "Premium" pill
 *   for CT55 / CT67 / LT90, the "Not Sure" dashed card, the auto-continue
 *   timer (300ms), the help-tip card, and the "Continue with all options"
 *   button when "Not Sure" is selected are all ported verbatim.
 *
 * MRT adaptations (documented in the SP3.A.3 brief):
 *   1. State comes from `useWizard()`. `selections.rigModel` is the
 *      Replace-path rig pick. Available threads = rig.threadSystem.primary
 *      + rig.threadSystem.alternates (deduped).
 *   2. Selection handler is `handleThreadChoiceSelect` from WizardContext,
 *      which already routes 'not_sure' → `threadSystem = null`. The
 *      300ms auto-continue navigates to HOLE_DIAMETER by calling the
 *      handler with the picked thread — `handleThreadChoiceSelect` in
 *      WizardContext also sets currentStep to HOLE_DIAMETER. For the
 *      "Not Sure" branch we delay calling the handler until the user
 *      clicks "Continue with all options".
 *   3. THREAD_DETAILS comes from `@data/wizardData` (port-first; do NOT
 *      redefine the map inline).
 *   4. lucide-react icons (`Check`, `HelpCircle`, `Lightbulb`, `ArrowLeft`,
 *      `ArrowRight`) replace TF v4's Material Symbols.
 *   5. Sandvik tokens; no rounded corners on cards.
 *
 * @module components/wizard/StepThreadChoice
 * @since 2026-05-18 (Group D Sub-Plan 3, Task SP3.A.3)
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Check, HelpCircle, Lightbulb, ArrowLeft, ArrowRight } from 'lucide-react';
import { useWizard } from '@contexts/WizardContext';
import { THREAD_DETAILS } from '@data/wizardData';
import { STEPS } from '@components/wizard/wizardConstants';

const AUTO_CONTINUE_MS = 300;

/**
 * Single thread option card. Ported from TF v4 StepThreadChoice.jsx
 * lines 96-158 (ThreadCard). The 16×16 thread badge uses inline
 * backgroundColor because the colour is data-driven (raw hex from
 * THREAD_DETAILS). Premium pill (sandvik-gold) renders when isPremium.
 */
function ThreadCard({ thread, isSelected, onSelect }) {
  const details = THREAD_DETAILS[thread] || {
    name: thread,
    size: `${thread} threads`,
    description: 'Standard thread system',
    holeRange: 'Various',
    color: '#1441F5',
  };
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      data-testid={`thread-card-${thread}`}
      onClick={() => onSelect(thread)}
      className={[
        'w-full p-5 border-2 transition-all duration-200 text-left bg-white',
        'focus:outline-none focus:ring-2 focus:ring-electric-blue',
        isSelected
          ? 'border-electric-blue bg-eb-1/20 shadow-md'
          : 'border-n2 hover:border-electric-blue hover:shadow-lg',
      ].join(' ')}
    >
      <div className="flex items-center gap-4">
        {/* Thread Badge — inline backgroundColor is intentional (data-driven). */}
        <div
          className="w-16 h-16 flex items-center justify-center flex-shrink-0 font-sandvik-headline font-normal text-xl text-white"
          style={{ backgroundColor: details.color }}
          aria-hidden="true"
        >
          {details.name}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-sandvik-headline font-normal text-lg text-carbon-black">
              {details.size}
            </h3>
            {details.isPremium && (
              <span
                data-testid="premium-badge"
                className="px-2 py-0.5 bg-sandvik-gold text-carbon-black font-sandvik-text text-xs font-normal"
              >
                Premium
              </span>
            )}
          </div>
          <p className="font-sandvik-text text-sm text-n6 mb-1">
            {details.description}
          </p>
          <p className="font-sandvik-text text-xs text-n5">
            Hole range: {details.holeRange}
          </p>
        </div>

        {isSelected && (
          <div
            className="flex-shrink-0 w-8 h-8 bg-electric-blue rounded-full flex items-center justify-center text-white"
            aria-hidden="true"
          >
            <Check size={18} aria-hidden="true" />
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * "Not Sure" card. Dashed border. TF v4 lines 161-194.
 */
function NotSureCard({ isSelected, onSelect }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      data-testid="thread-card-not-sure"
      onClick={() => onSelect('not_sure')}
      className={[
        'w-full p-5 border-2 border-dashed transition-all duration-200 text-left',
        'focus:outline-none focus:ring-2 focus:ring-electric-blue',
        isSelected
          ? 'border-electric-blue bg-eb-1/20'
          : 'border-n4 bg-n1 hover:border-electric-blue',
      ].join(' ')}
    >
      <div className="flex items-center gap-4">
        <div
          className="w-16 h-16 flex items-center justify-center flex-shrink-0 bg-n2 rounded-full text-n6"
          aria-hidden="true"
        >
          <HelpCircle size={28} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="font-sandvik-headline font-normal text-lg text-carbon-black mb-1">
            Not Sure
          </h3>
          <p className="font-sandvik-text text-sm text-n6">
            Show all thread options and let me filter later
          </p>
        </div>
        {isSelected && (
          <div
            className="flex-shrink-0 w-8 h-8 bg-electric-blue rounded-full flex items-center justify-center text-white"
            aria-hidden="true"
          >
            <Check size={18} aria-hidden="true" />
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * StepThreadChoice — Replace-path multi-thread picker.
 */
export default function StepThreadChoice() {
  const { selections, handleThreadChoiceSelect, setCurrentStep } = useWizard();
  const selectedRig = selections?.rigModel || null;

  // Local UI state for the selected thread (lets us show the checkmark
  // before WizardContext advances on auto-continue or "Not Sure" continue).
  const [selectedThread, setSelectedThread] = useState(null);
  const autoTimerRef = useRef(null);

  // Build the available thread list (port-first):
  //   - alternates from the rig (or empty),
  //   - prepend primary if it exists,
  //   - de-dup.
  const uniqueThreads = useMemo(() => {
    const alternates = selectedRig?.threadSystem?.alternates || [];
    const primary = selectedRig?.threadSystem?.primary;
    const list = primary ? [primary, ...alternates] : alternates;
    return [...new Set(list)];
  }, [selectedRig]);

  // Clear any pending auto-continue timer on unmount.
  useEffect(() => () => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
  }, []);

  const handleSelect = useCallback((thread) => {
    setSelectedThread(thread);
    // Clear any pending timer before queueing a new one.
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    if (thread !== 'not_sure') {
      // Auto-continue after 300ms (TF v4 line 234).
      autoTimerRef.current = setTimeout(() => {
        handleThreadChoiceSelect(thread);
      }, AUTO_CONTINUE_MS);
    }
  }, [handleThreadChoiceSelect]);

  const handleContinueNotSure = useCallback(() => {
    handleThreadChoiceSelect('not_sure');
  }, [handleThreadChoiceSelect]);

  const handleBack = useCallback(() => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    setCurrentStep(STEPS.EQUIPMENT_SELECT);
  }, [setCurrentStep]);

  const rockDrillLine = selectedRig?.rockDrill?.primary
    ? `${selectedRig.rockDrill.primary}${selectedRig.rockDrill.primaryPowerKw ? ` (${selectedRig.rockDrill.primaryPowerKw} kW)` : ''}`
    : '—';
  const holeRange = selectedRig?.holeDiameter?.minMm && selectedRig?.holeDiameter?.maxMm
    ? `${selectedRig.holeDiameter.minMm}-${selectedRig.holeDiameter.maxMm} mm`
    : '—';

  return (
    <section
      aria-labelledby="thread-choice-heading"
      className="max-w-2xl mx-auto px-4 py-8"
    >
      {/* Back */}
      <button
        type="button"
        data-testid="back-button"
        onClick={handleBack}
        className="inline-flex items-center gap-2 mb-8 text-electric-blue font-sandvik-text font-normal hover:text-carbon-black transition-colors"
      >
        <ArrowLeft size={20} aria-hidden="true" />
        Back
      </button>

      {/* Rig context */}
      <div className="bg-n1 p-4 mb-8" data-testid="rig-context">
        <p className="font-sandvik-text text-sm text-n6 mb-1">You selected:</p>
        <p className="font-sandvik-headline font-normal text-xl text-carbon-black">
          {selectedRig?.productName || '—'}
        </p>
        <p className="font-sandvik-text text-sm text-n6">
          Rock Drill: {rockDrillLine}
          {' · '}
          Hole Range: {holeRange}
        </p>
      </div>

      {/* Heading */}
      <header className="text-center mb-8">
        <h1
          id="thread-choice-heading"
          className="font-sandvik-headline font-normal text-2xl md:text-3xl text-carbon-black mb-4"
        >
          Which thread system are you using?
        </h1>
        <p className="font-sandvik-text text-n6">
          This rig supports multiple thread systems. Select yours for compatible tools.
        </p>
      </header>

      {/* Thread cards + Not Sure */}
      <div role="radiogroup" aria-label="Thread system" className="space-y-3 mb-6">
        {uniqueThreads.map((thread) => (
          <ThreadCard
            key={thread}
            thread={thread}
            isSelected={selectedThread === thread}
            onSelect={handleSelect}
          />
        ))}
        <NotSureCard
          isSelected={selectedThread === 'not_sure'}
          onSelect={handleSelect}
        />
      </div>

      {/* Help tip */}
      <div className="flex items-start gap-3 p-4 bg-eb-1/30 border-l-4 border-electric-blue" data-testid="help-tip">
        <Lightbulb size={20} className="text-electric-blue flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <p className="font-sandvik-text text-sm text-carbon-black font-normal mb-1">
            How to identify your thread system
          </p>
          <p className="font-sandvik-text text-sm text-n6">
            Check your shank adapter or current drill string. The thread type is usually
            marked on the component (e.g., &quot;T38&quot;, &quot;T45&quot;, or &quot;T51&quot;).
          </p>
        </div>
      </div>

      {/* Continue with all options — only when Not Sure is the active pick */}
      {selectedThread === 'not_sure' && (
        <div className="mt-8 text-center">
          <button
            type="button"
            data-testid="continue-not-sure"
            onClick={handleContinueNotSure}
            className="inline-flex items-center gap-2 px-8 py-3 bg-electric-blue text-white font-sandvik-text font-normal hover:bg-carbon-black transition-colors"
          >
            Continue with all options
            <ArrowRight size={20} aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
}
